import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useAuth } from "hooks/useAuth";
import { api } from "api/apiService";
import { loginUser, googleLogin } from "./services/loginService";
import AnimatedFaces from "./components/AnimatedFaces";

// Google Client ID з змінних оточення
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

// Куди йти після входу: ?next=/extension-auth повертає на сторінку, з якої
// прийшли. Шлях усередині сайту або https-адреса на піддомені m-tool.lol
// (VMS тощо) — туди йде спільна cookie сесії; будь-що інше — на головну.
const isSsoNext = (next) => /^https:\/\/[a-z0-9-]+\.m-tool\.lol(\/|$)/i.test(next || "");
const safeNext = (next) => {
  if (next && /^\/(?!\/)/.test(next)) return next;
  if (isSsoNext(next)) return next;
  return "/";
};

// Головний компонент сторінки "Логін"
const LoginPage = () => {
  // Стани для зберігання даних форми
  const [usernameOrEmail, setUsernameOrEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Реф для контейнера кнопки Google
  const googleButtonRef = useRef(null);

  // Хуки для навігації та доступу до глобального контексту авторизації
  const { login, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextPath = safeNext(searchParams.get("next"));

  // Усередині сайту — роутером; на піддомен — повним переходом, cookie вже стоїть
  const goNext = useCallback(() => {
    if (isSsoNext(nextPath)) window.location.assign(nextPath);
    else navigate(nextPath);
  }, [navigate, nextPath]);

  // Уже залогінений прийшов з піддомену: cookie могло не бути (вхід був до
  // її появи) — кладемо чинний токен у cookie і повертаємо без пароля
  useEffect(() => {
    if (authLoading || !isAuthenticated || !isSsoNext(nextPath)) return;
    api
      .post("/auth/session-cookie", {})
      .catch(() => {})
      .finally(() => window.location.assign(nextPath));
  }, [authLoading, isAuthenticated, nextPath]);

  // Обробник відповіді від Google Identity Services
  const handleGoogleResponse = useCallback(
    async (response) => {
      setError(null);
      setIsLoading(true);

      try {
        const authData = await googleLogin(response.credential);
        login(authData);
        goNext();
      } catch (err) {
        if (err.response && err.response.status === 404) {
          setError("Користувача з такою поштою Google не знайдено в системі.");
        } else if (err.response && err.response.status === 401) {
          setError("Не вдалося верифікувати Google акаунт.");
        } else {
          setError("Помилка авторизації через Google. Спробуйте пізніше.");
        }
      } finally {
        setIsLoading(false);
      }
    },
    [login, goNext],
  );

  // Ініціалізація Google Identity Services
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID || !window.google?.accounts?.id) return;

    window.google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: handleGoogleResponse,
    });

    window.google.accounts.id.renderButton(googleButtonRef.current, {
      type: "standard",
      theme: "filled_black",
      size: "large",
      text: "signin_with",
      shape: "pill",
      logo_alignment: "left",
      width: 300,
    });
  }, [handleGoogleResponse]);

  // Функція обробки відправлення форми
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      // Надсилаємо дані на сервер для автентифікації.
      const authData = await loginUser(usernameOrEmail, password);

      // Успіх: оновлюємо глобальний стан авторизації та перенаправляємо користувача.
      login(authData);
      goNext();
    } catch (err) {
      // Невдача: обробляємо помилки та виводимо відповідне повідомлення.
      if (err.response && err.response.status === 401) {
        setError("Неправильний логін або пароль. Спробуйте ще раз.");
      } else {
        setError("Виникла помилка. Будь ласка, спробуйте пізніше.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Функція обробки зміни значення полів
  const handleInputChange = (setter) => (e) => {
    setter(e.target.value);
    setError(null);
  };

  return (
    <main>
      <section id="lg">
        <div className="lg-l">
          <AnimatedFaces />
        </div>
        <div className="lg-r">
          <form onSubmit={handleSubmit}>
            <h1>Вхід до ЦАУ</h1>
            <input
              type="text"
              placeholder="Пошта або ім'я користувача"
              value={usernameOrEmail}
              onChange={handleInputChange(setUsernameOrEmail)}
              disabled={isLoading}
              required
            />
            <input
              type="password"
              placeholder="Пароль"
              value={password}
              onChange={handleInputChange(setPassword)}
              disabled={isLoading}
              required
            />
            <button className="focus-button" type="submit" disabled={isLoading}>
              {isLoading ? "Вхід..." : "Увійти"}
            </button>

            {GOOGLE_CLIENT_ID && (
              <>
                <div className="lg-divider">
                  <span>або</span>
                </div>
                <div
                  ref={googleButtonRef}
                  id="google-signin-button"
                  className="lg-google-btn"
                />
              </>
            )}

            {error && <p className="error-notification">{error}</p>}
          </form>
        </div>
      </section>
    </main>
  );
};

export default LoginPage;
